/**
 * MOKI's own analytics: every step of the page and the prelaunch app, kept in MOKI's D1 (table `events`).
 * Same design as the Shopify store's in-house tracking (a first-party batcher posting to our own
 * Worker, no third-party script), but a separate Worker and database, so nothing mixes with the store.
 *
 * Automatic: page views and time on page, how far people scroll, which sections they reach, every
 * click on an element with data-track, JavaScript errors, page speed, tab hidden/visible.
 * Manual: track("name", {props}) for the steps of the flow.
 */
import { getSession } from "./session";

type Props = Record<string, string | number | boolean | null | undefined>;
interface Queued { e: string; i: string; t: number; p: Props }

const ENDPOINT = "/api/collect";
const SESSION_KEY = "moki-session-v2";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const queue: Queued[] = [];
let timer: number | undefined;
let pageStartedAt = Date.now();
let maxScroll = 0;
let currentPath = "";
const seenSections = new Set<string>();

function id(): string {
  return crypto.randomUUID().replace(/-/g, "").slice(0, 24);
}

/** A visit: a new one after 30 minutes without activity. */
function sessionId(): { id: string; isNew: boolean } {
  const now = Date.now();
  try {
    const raw = sessionStorage.getItem(SESSION_KEY) ?? localStorage.getItem(SESSION_KEY);
    const saved = raw ? JSON.parse(raw) as { id: string; last: number } : null;
    const isNew = !saved || now - saved.last > SESSION_IDLE_MS;
    const next = { id: isNew ? id() : saved!.id, last: now };
    const text = JSON.stringify(next);
    try { sessionStorage.setItem(SESSION_KEY, text); } catch { /* private mode */ }
    try { localStorage.setItem(SESSION_KEY, text); } catch { /* private mode */ }
    return { id: next.id, isNew };
  } catch {
    return { id: "no-storage", isNew: false };
  }
}

/** Which app's in-app browser the visitor is in: this is how TikTok traffic shows itself. */
export function inAppBrowser(): string {
  const ua = navigator.userAgent;
  if (/musical_ly|TikTok|BytedanceWebview|trill/i.test(ua)) return "tiktok";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook";
  if (/Snapchat/i.test(ua)) return "snapchat";
  return "";
}

function device(): string {
  const w = window.innerWidth;
  return /Mobi|Android|iPhone/i.test(navigator.userAgent) || w < 700 ? "mobile" : w < 1100 ? "tablet" : "desktop";
}

function flush(useBeacon = false): void {
  window.clearTimeout(timer);
  timer = undefined;
  if (!queue.length) return;
  const session = getSession();
  const batch = queue.splice(0, 40);
  const body = JSON.stringify({
    s: sessionId().id,
    a: session.anonymousId,
    u: session.userId ?? null,
    ctx: {
      path: window.location.pathname,
      dev: device(),
      app: inAppBrowser(),
      ref: document.referrer ? (() => { try { return new URL(document.referrer).hostname; } catch { return ""; } })() : "",
      attr: session.attribution,
      w: window.innerWidth,
      lang: navigator.language,
    },
    events: batch,
  });
  if ((useBeacon || document.visibilityState === "hidden") && navigator.sendBeacon) {
    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
  } else {
    void fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => undefined);
  }
  if (queue.length) flush(useBeacon);
}

export function track(event: string, properties: Props = {}): string {
  const eventId = id();
  queue.push({ e: event.slice(0, 64), i: eventId, t: Date.now(), p: { ...properties, path: properties.path ?? window.location.pathname } });
  if (queue.length >= 20) flush();
  else if (timer === undefined) timer = window.setTimeout(() => flush(), 2500);
  return eventId;
}

function leavePage(): void {
  if (!currentPath) return;
  track("page_leave", { path: currentPath, seconds: Math.round((Date.now() - pageStartedAt) / 1000), max_scroll_pct: maxScroll });
}

/** Call on every route change (App does this). */
export function trackPageView(path: string): void {
  if (path === currentPath) return;
  leavePage();
  currentPath = path;
  pageStartedAt = Date.now();
  maxScroll = 0;
  seenSections.clear();
  const visit = sessionId();
  if (visit.isNew) track("session_start", { landing_path: path, in_app: inAppBrowser() || "none", device: device() });
  track("page_view", { path, title: document.title });
  window.setTimeout(observeSections, 400);
}

function observeSections(): void {
  if (!("IntersectionObserver" in window)) return;
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const name = (entry.target as HTMLElement).dataset.section;
      if (entry.isIntersecting && name && !seenSections.has(name)) {
        seenSections.add(name);
        track("section_view", { section: name, seconds_on_page: Math.round((Date.now() - pageStartedAt) / 1000) });
      }
    }
  }, { threshold: 0.35 });
  document.querySelectorAll<HTMLElement>("[data-section]").forEach((el) => observer.observe(el));
}

let started = false;
export function startTracking(): void {
  if (started) return;
  started = true;

  // Every click on something marked data-track="name" (buttons, links, cards).
  document.addEventListener("click", (event) => {
    const el = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-track]");
    if (!el) return;
    track("click", { target: el.dataset.track ?? "", label: (el.innerText || el.getAttribute("aria-label") || "").trim().slice(0, 60), section: el.closest<HTMLElement>("[data-section]")?.dataset.section ?? "" });
  }, { capture: true });

  // FAQ opens.
  document.addEventListener("toggle", (event) => {
    const d = event.target as HTMLDetailsElement;
    if (d.tagName === "DETAILS" && d.open) track("faq_open", { question: d.querySelector("summary")?.textContent?.replace(/\+$/, "").trim().slice(0, 80) ?? "" });
  }, true);

  // Scroll depth in 25% steps.
  window.addEventListener("scroll", () => {
    const h = document.documentElement.scrollHeight - window.innerHeight;
    if (h <= 0) return;
    const pct = Math.min(100, Math.round((window.scrollY / h) * 100));
    for (const mark of [25, 50, 75, 100]) {
      if (pct >= mark && maxScroll < mark) track("scroll_depth", { pct: mark });
    }
    maxScroll = Math.max(maxScroll, pct);
  }, { passive: true });

  // Errors, so a broken step shows up in the dashboard instead of silently losing people.
  window.addEventListener("error", (e) => track("js_error", { message: String(e.message).slice(0, 200), source: String(e.filename ?? "").split("/").pop() ?? "", line: e.lineno ?? 0 }));
  window.addEventListener("unhandledrejection", (e) => track("js_error", { message: String((e.reason && (e.reason.message || e.reason)) ?? "rejection").slice(0, 200) }));

  // Tab hidden / visible: during a Side Quest this is the phone being put down.
  document.addEventListener("visibilitychange", () => {
    track(document.visibilityState === "hidden" ? "tab_hidden" : "tab_visible", { seconds_on_page: Math.round((Date.now() - pageStartedAt) / 1000) });
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => { leavePage(); flush(true); });

  // Page speed: largest paint and first-load timing.
  try {
    new PerformanceObserver((list) => {
      const last = list.getEntries().at(-1);
      if (last) track("web_vital", { metric: "LCP", ms: Math.round(last.startTime) });
    }).observe({ type: "largest-contentful-paint", buffered: true });
  } catch { /* not supported */ }
  window.addEventListener("load", () => {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (nav) track("web_vital", { metric: "load", ms: Math.round(nav.loadEventStart), ttfb: Math.round(nav.responseStart) });
  });
}
